import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Bot,
  MapPin,
  Phone,
  Mail,
  CheckCircle,
  Target,
  Users,
  Lightbulb,
  Shield,
  Heart,
  ExternalLink,
  Calendar,
  BookOpen,
} from "lucide-react";
import { Navigation } from "./Navigation";
import ContactButton from "./ContactButton";
import ContactModal from "./ContactModal";
import BookingForm from "./BookingForm";
import CalWidget from "./CalWidget";
import QualificationAccordion from "./QualificationAccordion";
import SEO from "./SEO";
import SearchBox from "./SearchBox";
import BlogTimeline from "./BlogTimeline";
import { supabase } from "../lib/supabase";
import Footer from "./Footer";
import OptimizedImage from "./OptimizedImage";

export default function AboutPage() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [isCalOpen, setIsCalOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [heroImage, setHeroImage] = useState<string | null>(null);

  useEffect(() => {
    const fetchHeroImage = async () => {
      try {
        const { data: asset, error } = await supabase
          .from("assets")
          .select("url")
          .eq("id", "833d3177-3f06-44bc-9d2e-7cd0382146e0")
          .single();

        if (error) {
          console.error("Error querying asset:", error);
          return;
        }

        if (asset) {
          setHeroImage(asset.url);
        } else {
          console.warn("Hero image not found");
        }
      } catch (err) {
        console.error("Error fetching hero image:", err);
      }
    };

    fetchHeroImage();
  }, []);

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "One80Labs",
    description:
      "The Midwest's Premier AI Agency specializing in AI training, fractional project management, and technology consulting.",
    url: "https://one80labs.com",
    logo: "https://one80labs.com/logo.png",
    foundingDate: "2024",
    address: {
      "@type": "PostalAddress",
      streetAddress: "4383 Nicholas Street #302",
      addressLocality: "Omaha",
      addressRegion: "NE",
      postalCode: "68181",
      addressCountry: "US",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+1-402-915-3613",
      contactType: "customer service",
      email: "hello@one80labs.com",
      areaServed: "US",
    },
    sameAs: [
      "https://www.linkedin.com/company/one80labs",
      "https://twitter.com/one80labs",
    ],
    founder: {
      "@type": "Person",
      name: "Nick Kramer",
      jobTitle: "AI Productivity Specialist & Technology Educator",
      image:
        "https://raw.githubusercontent.com/ndkramer/One80Labs-Site/main/public/images/team/nick.kramer.png",
    },
  };

  return (
    <>
      <SEO
        title="About One80Labs - AI Training & Project Management Experts"
        description="Meet the team behind One80Labs, the Midwest's premier AI agency. Learn about our mission, values, and commitment to transforming businesses through AI and expert project management."
        image="https://raw.githubusercontent.com/ndkramer/One80Labs-Site/main/public/images/team/nick.kramer.png"
        jsonLd={organizationSchema}
        keywords={[
          "One80Labs",
          "AI agency",
          "technology education",
          "project management",
          "Nick Kramer",
          "AI training",
          "Midwest tech company",
          "technology consulting",
        ]}
      />
      <div className="min-h-screen bg-white">
        <Navigation onSearchClick={() => setShowSearch(!showSearch)} />
        {showSearch && (
          <div className="max-w-3xl mx-auto px-4 py-3">
            <SearchBox />
          </div>
        )}

        <main>
          {/* Origin Story Section */}
          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="bg-gray-50 rounded-lg overflow-hidden shadow-sm">
                <div className="flex flex-col md:flex-row">
                  <div className="md:w-1/2">
                    {heroImage ? (
                      <OptimizedImage
                        src={heroImage}
                        alt="The Midwest's Premier AI Agency"
                        width={800}
                        height={400}
                        hero={true}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full min-h-[300px] bg-gray-200 flex items-center justify-center">
                        <Bot className="h-12 w-12 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="p-8 md:w-1/2 flex flex-col justify-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      Our Story
                    </h2>
                    <p className="text-gray-600 mb-6">
                      Discover how One80Labs is emerging as the Midwest's
                      Premier AI Agency and our vision for transforming
                      businesses through AI innovation.
                    </p>
                    <Link
                      to="/blog/pivoting-future-one80labs-midwests-premier-ai-agency"
                      className="inline-flex items-center text-[#F98B3D] hover:text-[#e07a2c]"
                    >
                      Read more about why we started one80Labs
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl font-bold text-gray-900 text-center mb-12">
                Get in Touch
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                <div className="bg-white p-8 rounded-lg shadow-sm text-center">
                  <Phone className="h-8 w-8 text-[#F98B3D] mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Phone
                  </h3>
                  <p className="text-gray-600">
                    <a href="tel:+14029153613" className="hover:text-[#F98B3D]">
                      (402) 915-3613
                    </a>
                  </p>
                </div>

                <div className="bg-white p-8 rounded-lg shadow-sm text-center">
                  <Mail className="h-8 w-8 text-[#F98B3D] mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Email
                  </h3>
                  <p className="text-gray-600">
                    <a
                      href="mailto:hello@one80labs.com"
                      className="hover:text-[#F98B3D]"
                    >
                      hello@one80labs.com
                    </a>
                  </p>
                </div>

                <div className="bg-white p-8 rounded-lg shadow-sm text-center">
                  <MapPin className="h-8 w-8 text-[#F98B3D] mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Address
                  </h3>
                  <p className="text-gray-600">
                    4383 Nicholas Street #302
                    <br />
                    Omaha, NE 68181
                  </p>
                </div>
              </div>

              <div className="rounded-lg overflow-hidden shadow-sm">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2999.3176080142147!2d-95.9397187!3d41.2651675!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87938dc0123cde99%3A0x384d8a9c3c5b37e2!2s4383%20Nicholas%20St%20%23302%2C%20Omaha%2C%20NE%2068131!5e0!3m2!1sen!2sus!4v1709734844045!5m2!1sen!2sus"
                  width="100%"
                  height="450"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </section>

          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="bg-gray-300 rounded-lg shadow-xl overflow-hidden">
                <div className="px-6 py-12 md:p-12 text-center">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Ready to Transform Your Project Management?
                  </h2>
                  <p className="text-base text-gray-700 mb-8 max-w-2xl mx-auto">
                    Experience the power of AI-driven project management with
                    our Fractional PM services. Let's discuss how we can help
                    streamline your operations and drive success.
                  </p>
                  <div className="flex flex-col sm:flex-row justify-center gap-4">
                    <Link
                      to="/fractional-pm"
                      className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-black bg-white hover:bg-gray-100 transition-colors duration-200"
                    >
                      <ExternalLink className="h-5 w-5 mr-2" />
                      Learn About Fractional PM
                    </Link>
                    <button
                      onClick={() => setIsCalOpen(true)}
                      className="inline-flex items-center px-6 py-3 border border-gray-700 text-base font-medium rounded-md text-gray-900 hover:bg-white hover:text-black transition-colors duration-200"
                    >
                      <Calendar className="h-5 w-5 mr-2" />
                      Schedule a Consultation
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-8">
                Meet The Founder
              </h2>
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="md:w-1/3">
                    <OptimizedImage
                      src="https://raw.githubusercontent.com/ndkramer/One80Labs-Site/main/public/images/team/nick.kramer.png"
                      alt="Nick Kramer"
                      width={816}
                      height={459}
                      className="rounded-lg w-full h-auto"
                    />
                  </div>
                  <div className="md:w-2/3">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Nick Kramer
                    </h3>
                    <p className="text-base text-gray-600 italic mb-4">
                      AI Productivity Specialist & Technology Educator
                    </p>
                    <div className="prose max-w-none text-gray-600">
                      <p>
                        Nick is a technology educator and AI specialist with
                        over 15 years of experience helping professionals and
                        organizations leverage emerging technologies to enhance
                        productivity and innovation.
                      </p>
                      <p>
                        As the founder of One80Labs and One80Training, Nick has
                        worked with businesses across industries—ranging from
                        finance and healthcare to manufacturing and tech—guiding
                        teams in leveraging AI for automation, data analysis,
                        and workflow optimization. He specializes in making AI
                        accessible to non-technical professionals, ensuring they
                        can harness its power for smarter decision-making and
                        productivity gains.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="bg-white rounded-lg shadow-xl overflow-hidden">
                <div className="px-6 py-12 md:p-12 text-center">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Is AI the Missing Piece in Your Business?
                  </h2>
                  <p className="text-base text-gray-700 mb-8 max-w-2xl mx-auto">
                    Discover how AI Agents can automate tasks, boost efficiency,
                    and unlock new opportunities—without the complexity. Let's
                    explore what's possible for your business.
                  </p>
                  <button
                    onClick={() => setIsCalOpen(true)}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#F98B3D] hover:bg-[#e07a2c] transition-colors duration-200"
                  >
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Book Your Free AI Strategy Call
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">
                Why One80Labs is Uniquely Qualified to Help you Succeed
              </h2>
              <p className="text-base text-gray-600 text-center max-w-2xl mx-auto mb-12">
                Our unique approach combines deep technical expertise with a
                focus on practical, results-driven solutions. Here's what sets
                us apart:
              </p>

              <QualificationAccordion />
            </div>
          </section>

          <section className="py-16 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">
                Our Prime Directive
              </h2>
              <p className="text-base text-gray-600 text-center max-w-2xl mx-auto mb-12">
                At One80Labs, our mission is guided by core values that ensure
                we deliver exceptional results while maintaining the highest
                standards of professionalism and innovation.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-all duration-300 group">
                  <div className="flex items-center mb-4">
                    <div className="bg-gray-100 p-3 rounded-lg">
                      <Target className="h-8 w-8 text-gray-900" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 ml-4">
                      Excellence in Execution
                    </h3>
                  </div>
                  <p className="text-base text-gray-600">
                    We are committed to delivering the highest quality
                    solutions. Every project is an opportunity to exceed
                    expectations and create lasting value for our clients.
                  </p>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-all duration-300 group">
                  <div className="flex items-center mb-4">
                    <div className="bg-gray-100 p-3 rounded-lg">
                      <Users className="h-8 w-8 text-gray-900" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 ml-4">
                      Client-Centric Focus
                    </h3>
                  </div>
                  <p className="text-base text-gray-600">
                    Your success is our success. We work closely with each
                    client to understand their unique needs and deliver tailored
                    solutions that drive real business outcomes.
                  </p>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-all duration-300 group">
                  <div className="flex items-center mb-4">
                    <div className="bg-gray-100 p-3 rounded-lg">
                      <Lightbulb className="h-8 w-8 text-gray-900" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 ml-4">
                      Innovation First
                    </h3>
                  </div>
                  <p className="text-base text-gray-600">
                    We stay at the forefront of technology, constantly exploring
                    new ways to leverage AI and automation to enhance
                    productivity and drive business growth.
                  </p>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-all duration-300 group">
                  <div className="flex items-center mb-4">
                    <div className="bg-gray-100 p-3 rounded-lg">
                      <Shield className="h-8 w-8 text-gray-900" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 ml-4">
                      Trusted Partnership
                    </h3>
                  </div>
                  <p className="text-base text-gray-600">
                    We build long-term relationships based on transparency,
                    integrity, and mutual respect. Your trust is our most
                    valuable asset.
                  </p>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-all duration-300 group">
                  <div className="flex items-center mb-4">
                    <div className="bg-gray-100 p-3 rounded-lg">
                      <Heart className="h-8 w-8 text-gray-900" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 ml-4">
                      Passion for Impact
                    </h3>
                  </div>
                  <p className="text-base text-gray-600">
                    We're passionate about making a difference. Every project is
                    an opportunity to create positive change and help businesses
                    reach their full potential.
                  </p>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-all duration-300 group">
                  <div className="flex items-center mb-4">
                    <div className="bg-gray-100 p-3 rounded-lg">
                      <Bot className="h-8 w-8 text-gray-900" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 ml-4">
                      AI-Driven Excellence
                    </h3>
                  </div>
                  <p className="text-base text-gray-600">
                    We harness the power of AI to transform businesses,
                    automating workflows and unlocking new opportunities for
                    growth and innovation.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="bg-gray-300 rounded-lg shadow-xl overflow-hidden">
                <div className="px-6 py-12 md:p-12 text-center">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Unlock Your AI Potential with Expert Training
                  </h2>
                  <p className="text-base text-gray-700 mb-8 max-w-2xl mx-auto">
                    Master AI tools like ChatGPT, Gemini, and Microsoft 365
                    Copilot. Join our hands-on workshops and transform the way
                    you work with cutting-edge AI technology.
                  </p>
                  <div className="flex flex-col sm:flex-row justify-center gap-4">
                    <Link
                      to="/course-outline"
                      className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-gray-900 bg-white hover:bg-gray-100 transition-colors duration-200"
                    >
                      <BookOpen className="h-5 w-5 mr-2" />
                      View Training Programs
                    </Link>
                    <button
                      onClick={() => setIsContactModalOpen(true)}
                      className="inline-flex items-center px-6 py-3 border border-gray-700 text-base font-medium rounded-md text-gray-900 hover:bg-white hover:text-black transition-colors duration-200"
                    >
                      <Mail className="h-5 w-5 mr-2" />
                      Contact Us
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Latest Updates Timeline Section */}
          <div className="bg-white py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Blog Timeline
              </h2>
              <BlogTimeline />
            </div>
          </div>
        </main>

        <Footer />

        <ContactButton
          onClick={() => setIsContactModalOpen(true)}
          onCalendarClick={() => setIsBookingOpen(true)}
          onCalClick={() => setIsCalOpen(true)}
        />

        <ContactModal
          isOpen={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
          // Using Netlify Forms - no onSubmit prop needed
        />

        <BookingForm
          isOpen={isBookingOpen}
          onClose={() => setIsBookingOpen(false)}
        />

        <CalWidget isOpen={isCalOpen} onClose={() => setIsCalOpen(false)} />
      </div>
    </>
  );
}
